import { assoc, dissoc, has, pipe, prop, when } from 'ramda';

import { createCacheUpdateHandler } from 'utils/helpers/fetch';
import { createSingleAbortableQuery } from 'utils/helpers/abortPreviousRequest';
import { loadingManagementApi } from '../../loadingManagementApi';

const getUrl = suffix => `carrier-authorizations/${suffix}`;

// this can be expanded in the future to remap other params as needed,
// but for now we just need to remap pageSize to results
const remapParams = when(
  has('pageSize'),
  pipe(params => assoc('results', prop('pageSize', params), params), dissoc('pageSize'))
);

// Controller ref for cancelling previous carrier auth list requests
const carrierAuthListController = { current: null };

const carrierAuthorizationsEndpoints = loadingManagementApi.injectEndpoints({
  endpoints: ({ mutation, query }) => ({
    exportCarrierAuthorizations: query({
      query: params => ({
        params,
        responseHandler: response => response.blob(),
        url: getUrl('export'),
      }),
    }),
    getCarrierAuthCarriers: query({
      query: ({ params } = {}) => ({
        params,
        url: getUrl('get-unique-carriers'),
      }),
    }),
    getCarrierAuthLocations: query({
      query: ({ params } = {}) => ({
        params,
        url: getUrl('get-filter-locations'),
      }),
    }),
    getCarrierAuthorizationsList: query({
      providesTags: ['CarrierAuthorizationList'],
      queryFn: createSingleAbortableQuery(
        carrierAuthListController,
        async ({ params }, api, extraOptions, baseQuery, signal) => {
          const remappedParams = remapParams(params);
          return baseQuery(
            {
              body: remappedParams,
              method: 'POST',
              signal,
              url: getUrl('get-carrier-authorizations-list'),
            },
            api,
            extraOptions
          );
        }
      ),
    }),
    removeBulkCarrierAuthorizations: mutation({
      onQueryStarted: createCacheUpdateHandler(
        loadingManagementApi,
        'getCarrierAuthorizationsList',
        (draft, { carrierAuthorizationIds }) => {
          if (draft.carrierAuthorizations) {
            const idsToRemove = new Set(carrierAuthorizationIds);
            for (let i = draft.carrierAuthorizations.length - 1; i >= 0; i -= 1) {
              if (idsToRemove.has(draft.carrierAuthorizations[i].carrierAuthorizationId)) {
                draft.carrierAuthorizations.splice(i, 1);
              }
            }
            if (draft.totalResults !== undefined) {
              // eslint-disable-next-line no-param-reassign
              draft.totalResults -= carrierAuthorizationIds.length;
            }
          }
        },
        { invalidateOnError: ['CarrierAuthorizationList'] }
      ),
      query: ({ carrierAuthorizationIds }) => ({
        body: { carrierAuthorizationIds },
        method: 'POST',
        url: getUrl('remove-bulk-carrier-authorizations'),
      }),
    }),
    removeCarrierAuthorization: mutation({
      /**
       * Uses createCacheUpdateHandler to update the cache without triggering a full data refetch.
       * This provides instant UI feedback when removing a carrier authorization.
       *
       * Parameters:
       * 1. API instance (loadingManagementApi)
       * 2. Query endpoint name to update ('getCarrierAuthorizationsList')
       * 3. Update function: receives (draft, mutationArgs) and removes the deleted item from cached data
       * 4. Options: { invalidateOnError: [...tags] } - tags to invalidate if mutation fails
       *
       * The helper automatically:
       * - Waits for mutation success (pessimistic update)
       * - Finds all cached queries for the endpoint (handles pagination/sorting)
       * - Updates each cache by filtering out the removed authorization
       * - Falls back to tag invalidation on error
       */
      onQueryStarted: createCacheUpdateHandler(
        loadingManagementApi,
        'getCarrierAuthorizationsList',
        (draft, { carrierAuthorizationId }) => {
          if (draft.carrierAuthorizations) {
            const indexToRemove = draft.carrierAuthorizations.findIndex(
              auth => auth.carrierAuthorizationId === carrierAuthorizationId
            );
            if (indexToRemove !== -1) {
              draft.carrierAuthorizations.splice(indexToRemove, 1);
              // Update total count if it exists
              if (draft.totalResults !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.totalResults -= 1;
              }
            }
          }
        },
        { invalidateOnError: ['CarrierAuthorizationList'] }
      ),
      query: ({ carrierAuthorizationId, supplier }) => ({
        body: { carrierAuthorizationId, supplier },
        method: 'POST',
        url: getUrl('remove'),
      }),
    }),
  }),
});

export const {
  useGetCarrierAuthCarriersQuery,
  useGetCarrierAuthLocationsQuery,
  useGetCarrierAuthorizationsListQuery,
  useLazyExportCarrierAuthorizationsQuery,
  useLazyGetCarrierAuthorizationsListQuery,
  useRemoveBulkCarrierAuthorizationsMutation,
  useRemoveCarrierAuthorizationMutation,
} = carrierAuthorizationsEndpoints;
